import { useState, useEffect, useCallback, useRef } from 'react';
import { startKyc, waitForKyc } from '../lib/kyc';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { toast } from 'sonner';
import { 
  Shield, 
  Lock, 
  Unlock,
  AlertTriangle,
  Camera,
  CheckCircle2,
  XCircle,
  Loader2,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '../components/ui/input-otp';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// Partner logo colors
const PARTNER_COLORS = {
  gcash: '#007DFE',
  maya: '#00D084',
  grabpay: '#00B14F',
  bdo: '#0066B3',
  bpi: '#A81C32',
  unionbank: '#FF6B00',
};

// Lock status badge component
const LockStatusBadge = ({ status }) => {
  const config = {
    SAFE: { bg: 'bg-[#10B981]/20', border: 'border-[#10B981]/30', text: 'text-[#10B981]', label: 'SAFE' },
    PARTIAL_LOCK: { bg: 'bg-[#FFCC00]/20', border: 'border-[#FFCC00]/30', text: 'text-[#FFCC00]', label: 'PARTIAL LOCK' },
    FULL_LOCK: { bg: 'bg-[#FF3B30]/20', border: 'border-[#FF3B30]/30', text: 'text-[#FF3B30]', label: 'FULL LOCK' },
  };
  const c = config[status] || config.SAFE;
  
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold ${c.bg} ${c.border} ${c.text} border`}>
      {c.label}
    </span>
  );
};

export default function RecoveryPage() {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lockStatus, setLockStatus] = useState('FULL_LOCK');
  const [recoveryStep, setRecoveryStep] = useState('init'); // init, kyc, unlock
  const [kycStatus, setKycStatus] = useState('idle');
  const [kycFrameUrl, setKycFrameUrl] = useState(null);
  const kycRunRef = useRef(0);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showUnlockCardModal, setShowUnlockCardModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [unlockPin, setUnlockPin] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await api.get('/panic/status');
      setPartners(response.data.partners);
      setCards(response.data.cards || []);
      setLockStatus(response.data.lock_status);
      
      // Check cooldown
      if (user?.recovery_cooldown_until) {
        const cooldownEnd = new Date(user.recovery_cooldown_until);
        const now = new Date();
        if (cooldownEnd > now) {
          setCooldownRemaining(Math.ceil((cooldownEnd - now) / 1000));
        }
      }
    } catch (error) {
      toast.error('Failed to load recovery data');
    } finally {
      setLoading(false);
    }
  }, [api, user]);

  useEffect(() => {
    if (!user?.panic_mode) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate, fetchData]);

  // Cooldown timer
  useEffect(() => {
    if (cooldownRemaining > 0) {
      const timer = setInterval(() => {
        setCooldownRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [cooldownRemaining]);

  const formatCooldown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const initiateRecovery = async () => {
    try {
      await api.post('/recovery/initiate');
      setRecoveryStep('kyc');
      startKycVerification();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate recovery');
    }
  };

  const startKycVerification = async () => {
    setKycStatus('scanning');
    setKycFrameUrl(null);
    const run = ++kycRunRef.current;

    try {
      const started = await startKyc(api);
      if (started.provider === 'ekyc') {
        setKycFrameUrl(started.url);
        const result = await waitForKyc(api, () => kycRunRef.current !== run);
        if (result === 'cancelled') return;
        setKycFrameUrl(null);
        if (result === 'verified') {
          setKycStatus('success');
          setRecoveryStep('unlock');
          toast.success('Identity verified');
        } else {
          setKycStatus('failed');
          toast.error(result?.message || 'Verification did not complete. Please try again.');
        }
        return;
      }
    } catch (err) {
      setKycFrameUrl(null);
      setKycStatus('failed');
      toast.error(err.response?.data?.detail || 'Could not start verification');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      // Simulate KYC verification
      setTimeout(async () => {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
        try {
          // The server records the verification; the client cannot assert it
          await api.post('/recovery/verify-kyc');
          setKycStatus('success');
          setRecoveryStep('unlock');
          toast.success('KYC verification passed');
        } catch (err) {
          setKycStatus('failed');
          toast.error(err.response?.data?.detail || 'Verification failed');
        }
      }, 3000);
    } catch (error) {
      console.error('Camera access denied:', error);
      setKycStatus('failed');
      toast.error('Camera access denied');
    }
  };

  const openUnlockModal = (partner) => {
    setSelectedPartner(partner);
    setTotpCode('');
    setUnlockPin('');
    setShowUnlockModal(true);
  };

  const handleUnlock = async () => {
    if (!selectedPartner) return;

    if (user?.mfa_enabled && totpCode.length !== 6) {
      toast.error('Please enter your 2FA code');
      return;
    }
    if (!user?.mfa_enabled && unlockPin.length < 6) {
      toast.error('Please enter your panic PIN');
      return;
    }

    setUnlocking(true);
    try {
      const response = await api.post('/recovery/unlock-partner', {
        partner_id: selectedPartner.id,
        totp_code: totpCode,
        panic_pin: unlockPin,
      });

      toast.success(response.data.message);
      setShowUnlockModal(false);
      
      // Update partners list
      setPartners(partners.map(p => 
        p.id === selectedPartner.id ? { ...p, locked: false } : p
      ));

      // Check if recovery complete
      if (!response.data.panic_mode) {
        toast.success('All items recovered! System is now safe.');
        await refreshUser();
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock');
      setTotpCode('');
    } finally {
      setUnlocking(false);
    }
  };

  const openUnlockCardModal = (card) => {
    setSelectedCard(card);
    setTotpCode('');
    setUnlockPin('');
    setShowUnlockCardModal(true);
  };

  const handleUnlockCard = async () => {
    if (!selectedCard) return;

    if (user?.mfa_enabled && totpCode.length !== 6) {
      toast.error('Please enter your 2FA code');
      return;
    }
    if (!user?.mfa_enabled && unlockPin.length < 6) {
      toast.error('Please enter your panic PIN');
      return;
    }

    setUnlocking(true);
    try {
      const response = await api.post('/recovery/unlock-card', {
        card_id: selectedCard.id,
        totp_code: totpCode,
        panic_pin: unlockPin,
      });

      toast.success(response.data.message);
      setShowUnlockCardModal(false);
      
      // Update cards list
      setCards(cards.map(c => 
        c.id === selectedCard.id ? { ...c, status: 'active', locked_at: null } : c
      ));

      // Check if recovery complete
      if (!response.data.panic_mode) {
        toast.success('All items recovered! System is now safe.');
        await refreshUser();
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock card');
      setTotpCode('');
    } finally {
      setUnlocking(false);
    }
  };

  const completeRecovery = async () => {
    try {
      await api.post('/recovery/complete');
      toast.success('Recovery completed successfully!');
      await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete recovery');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-12 h-12"></div>
        </div>
      </Layout>
    );
  }

  const lockedPartners = partners.filter(p => p.locked);
  const unlockedPartners = partners.filter(p => !p.locked);
  const protectedPartners = partners.filter(p => p.protected);
  const lockedCards = cards.filter(c => c.status === 'panic_locked');
  const unlockedCards = cards.filter(c => c.status === 'active');

  const totalLocked = lockedPartners.length + lockedCards.length;

  return (
    <Layout>
      <div className="space-y-8" data-testid="recovery-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl lg:text-4xl font-black text-[#E6F1FF]">Recovery Mode</h1>
              <LockStatusBadge status={lockStatus} />
            </div>
            <p className="text-[#8892B0]">Unlock your protected accounts and cards</p>
          </div>
          
          {totalLocked === 0 && (
            <Button
              onClick={completeRecovery}
              className="bg-[#10B981] hover:bg-[#059669] text-white font-bold px-8 py-6 rounded-xl shadow-lg shadow-[#10B981]/30"
              data-testid="complete-recovery-button"
            >
              <CheckCircle2 className="w-5 h-5 mr-2" />
              Complete Recovery
            </Button>
          )}
        </div>

        {/* Status Banner */}
        <div className={`
          p-6 rounded-2xl
          ${lockStatus === 'FULL_LOCK' 
            ? 'bg-gradient-to-r from-[#FF3B30]/20 to-[#FF3B30]/5 border border-[#FF3B30]/30' 
            : 'bg-gradient-to-r from-[#FFCC00]/20 to-[#FFCC00]/5 border border-[#FFCC00]/30'
          }
        `}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${lockStatus === 'FULL_LOCK' ? 'bg-[#FF3B30]/20' : 'bg-[#FFCC00]/20'}`}>
              <Lock className={`w-7 h-7 ${lockStatus === 'FULL_LOCK' ? 'text-[#FF3B30]' : 'text-[#FFCC00]'}`} />
            </div>
            <div className="flex-1">
              <h2 className={`text-xl font-bold ${lockStatus === 'FULL_LOCK' ? 'text-[#FF3B30]' : 'text-[#FFCC00]'}`}>
                {lockStatus === 'FULL_LOCK' ? 'System Locked' : 'Partially Locked'}
              </h2>
              <p className="text-[#8892B0] text-sm mt-1">
                {lockedPartners.length} of {protectedPartners.length} protected accounts still locked. 
                Complete verification to unlock each account.
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#8892B0]">Progress</p>
              <p className="text-lg font-bold text-[#E6F1FF]">
                {protectedPartners.length - lockedPartners.length}/{protectedPartners.length} Unlocked
              </p>
            </div>
          </div>
        </div>

        {/* Cooldown Warning */}
        {cooldownRemaining > 0 && recoveryStep === 'init' && (
          <div className="p-4 rounded-xl bg-[#FFCC00]/10 border border-[#FFCC00]/30 flex items-center gap-4">
            <Clock className="w-6 h-6 text-[#FFCC00]" />
            <div>
              <p className="text-sm font-medium text-[#FFCC00]">Cooldown Active</p>
              <p className="text-xs text-[#8892B0]">
                Recovery available in {formatCooldown(cooldownRemaining)}
              </p>
            </div>
          </div>
        )}

        {/* Recovery Steps */}
        {recoveryStep === 'init' && cooldownRemaining === 0 && (
          <div className="glass-card rounded-2xl p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#2BBBAD]/20 flex items-center justify-center">
              <Shield className="w-10 h-10 text-[#2BBBAD]" />
            </div>
            <h2 className="text-2xl font-bold text-[#E6F1FF] mb-2">Start Recovery Process</h2>
            <p className="text-[#8892B0] mb-6 max-w-md mx-auto">
              To unlock your accounts, you'll need to complete KYC verification 
              {user?.mfa_enabled && ' and provide your 2FA code for each account'}.
            </p>
            <Button
              onClick={initiateRecovery}
              className="bg-[#2BBBAD] hover:bg-[#3FD2C7] text-[#0B1C2D] font-bold px-8 py-4 rounded-xl shadow-lg shadow-[#2BBBAD]/30"
              data-testid="initiate-recovery-button"
            >
              Begin Verification
            </Button>
          </div>
        )}

        {/* KYC Verification */}
        {recoveryStep === 'kyc' && (
          <div className="glass-card rounded-2xl p-8">
            <h2 className="text-xl font-bold text-[#E6F1FF] mb-6 text-center">
              KYC Liveness Verification
            </h2>
            
            <div className={kycFrameUrl ? 'mx-auto mb-6 w-full' : 'kyc-camera mx-auto mb-6'}>
              {kycFrameUrl && kycStatus === 'scanning' && (
                <iframe
                  title="Identity verification"
                  src={kycFrameUrl}
                  allow="camera; microphone"
                  className="w-full border-0 rounded-lg bg-white"
                  style={{ height: 520 }}
                  data-testid="kyc-frame"
                />
              )}
{kycStatus === 'scanning' && !kycFrameUrl && (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-32 h-32 border-4 border-[#2BBBAD] rounded-full animate-pulse" />
                  </div>
                </>
              )}
              {kycStatus === 'success' && (
                <div className="w-full h-full flex items-center justify-center bg-[#10B981]/10">
                  <CheckCircle2 className="w-20 h-20 text-[#10B981]" />
                </div>
              )}
              {kycStatus === 'failed' && (
                <div className="w-full h-full flex items-center justify-center bg-[#FF3B30]/10">
                  <XCircle className="w-20 h-20 text-[#FF3B30]" />
                </div>
              )}
            </div>

            <p className="text-center text-[#8892B0]">
              {kycStatus === 'scanning' && (kycFrameUrl ? 'Follow the steps above to verify your identity' : 'Please look at the camera...')}
              {kycStatus === 'success' && 'Verification successful! Proceeding to unlock...'}
              {kycStatus === 'failed' && 'Verification failed. Please try again.'}
            </p>

            {kycStatus === 'failed' && (
              <div className="flex justify-center mt-4">
                <Button
                  onClick={startKycVerification}
                  className="bg-[#2BBBAD] hover:bg-[#3FD2C7] text-[#0B1C2D] font-bold"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Retry
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Unlock Partners */}
        {recoveryStep === 'unlock' && (
          <>
            {/* Locked Partners */}
            {lockedPartners.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-[#E6F1FF] mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-[#FF3B30]" />
                  Locked Accounts ({lockedPartners.length})
                </h3>
                <div className="partners-grid">
                  {lockedPartners.map((partner) => (
                    <div 
                      key={partner.id}
                      className="partner-card locked"
                      data-testid={`locked-partner-${partner.code}`}
                    >
                      <div 
                        className="partner-logo mb-4"
                        style={{ backgroundColor: PARTNER_COLORS[partner.code] || '#2BBBAD' }}
                      >
                        {partner.name.charAt(0)}
                      </div>
                      <h4 className="text-lg font-bold text-[#E6F1FF] mb-1">{partner.name}</h4>
                      <p className="text-sm text-[#FF3B30] mb-4">Account Locked</p>
                      <Button
                        onClick={() => openUnlockModal(partner)}
                        className="w-full bg-[#FF3B30]/20 hover:bg-[#FF3B30]/30 text-[#FF3B30] border border-[#FF3B30]/30"
                        data-testid={`unlock-button-${partner.code}`}
                      >
                        <Unlock className="w-4 h-4 mr-2" />
                        Unlock
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Unlocked Partners */}
            {unlockedPartners.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-[#E6F1FF] mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                  Recovered Accounts ({unlockedPartners.length})
                </h3>
                <div className="partners-grid">
                  {unlockedPartners.map((partner) => (
                    <div 
                      key={partner.id}
                      className="partner-card opacity-60"
                      data-testid={`unlocked-partner-${partner.code}`}
                    >
                      <div 
                        className="partner-logo mb-4"
                        style={{ backgroundColor: PARTNER_COLORS[partner.code] || '#2BBBAD' }}
                      >
                        {partner.name.charAt(0)}
                      </div>
                      <h4 className="text-lg font-bold text-[#E6F1FF] mb-1">{partner.name}</h4>
                      <p className="text-sm text-[#10B981] flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" />
                        Recovered
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Locked Cards */}
            {lockedCards.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-[#E6F1FF] mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-[#FF3B30]" />
                  Locked Cards ({lockedCards.length})
                </h3>
                <div className="partners-grid">
                  {lockedCards.map((card) => (
                    <div 
                      key={card.id}
                      className="partner-card locked"
                      data-testid={`locked-card-${card.last4}`}
                    >
                      <div 
                        className="partner-logo mb-4"
                        style={{ backgroundColor: PARTNER_COLORS[card.bank_partner_id] || '#2BBBAD' }}
                      >
                        {card.bank_name?.charAt(0) || 'B'}
                      </div>
                      <h4 className="text-lg font-bold text-[#E6F1FF] mb-1">
                        {card.bank_name} {card.card_type.charAt(0).toUpperCase() + card.card_type.slice(1)}
                      </h4>
                      <p className="text-sm text-[#8892B0] font-mono mb-2">•••• {card.last4}</p>
                      <p className="text-sm text-[#FF3B30] mb-4">Card Frozen</p>
                      <Button
                        onClick={() => openUnlockCardModal(card)}
                        className="w-full bg-[#FF3B30]/20 hover:bg-[#FF3B30]/30 text-[#FF3B30] border border-[#FF3B30]/30"
                        data-testid={`unlock-card-${card.last4}`}
                      >
                        <Unlock className="w-4 h-4 mr-2" />
                        Unlock Card
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Unlocked Cards */}
            {unlockedCards.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-[#E6F1FF] mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                  Recovered Cards ({unlockedCards.length})
                </h3>
                <div className="partners-grid">
                  {unlockedCards.map((card) => (
                    <div 
                      key={card.id}
                      className="partner-card opacity-60"
                      data-testid={`unlocked-card-${card.last4}`}
                    >
                      <div 
                        className="partner-logo mb-4"
                        style={{ backgroundColor: PARTNER_COLORS[card.bank_partner_id] || '#2BBBAD' }}
                      >
                        {card.bank_name?.charAt(0) || 'B'}
                      </div>
                      <h4 className="text-lg font-bold text-[#E6F1FF] mb-1">
                        {card.bank_name} {card.card_type.charAt(0).toUpperCase() + card.card_type.slice(1)}
                      </h4>
                      <p className="text-sm text-[#8892B0] font-mono mb-2">•••• {card.last4}</p>
                      <p className="text-sm text-[#10B981] flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" />
                        Active
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Unlock Partner Modal */}
      <Dialog open={showUnlockModal} onOpenChange={setShowUnlockModal}>
        <DialogContent className="bg-[#112240] border-[#64FFDA]/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#E6F1FF] text-center">
              Unlock {selectedPartner?.name}
            </DialogTitle>
            <DialogDescription className="text-center text-[#8892B0]">
              {user?.mfa_enabled 
                ? 'Enter your 2FA code to unlock this account'
                : 'Enter your panic PIN to unlock this account'
              }
            </DialogDescription>
          </DialogHeader>

          {user?.mfa_enabled && (
            <div className="py-4">
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={totpCode}
                  onChange={setTotpCode}
                  data-testid="unlock-otp-input"
                >
                  <InputOTPGroup className="gap-2">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <InputOTPSlot
                        key={index}
                        index={index}
                        className="w-12 h-14 text-xl font-bold bg-[#0B1C2D] border-[#2BBBAD]/30 text-[#E6F1FF] rounded-lg"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>
          )}

          {!user?.mfa_enabled && (
            <div className="py-4 flex justify-center">
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={12}
                value={unlockPin}
                onChange={(e) => setUnlockPin(e.target.value.replace(/\D/g, ''))}
                placeholder="Panic PIN"
                className="w-56 h-14 text-center text-xl tracking-widest bg-[#0B1C2D] border border-[#2BBBAD]/30 text-[#E6F1FF] rounded-lg"
                data-testid="unlock-pin-input"
              />
            </div>
          )}

          <DialogFooter className="flex gap-3 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setShowUnlockModal(false)}
              className="flex-1 border-[#64FFDA]/20 text-[#8892B0] hover:bg-[#64FFDA]/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUnlock}
              disabled={unlocking || (user?.mfa_enabled ? totpCode.length !== 6 : unlockPin.length < 6)}
              className="flex-1 bg-[#10B981] hover:bg-[#059669] text-white font-bold"
              data-testid="confirm-unlock-button"
            >
              {unlocking ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Unlock className="w-4 h-4 mr-2" />
                  Unlock
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlock Card Modal */}
      <Dialog open={showUnlockCardModal} onOpenChange={setShowUnlockCardModal}>
        <DialogContent className="bg-[#112240] border-[#64FFDA]/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#E6F1FF] text-center">
              Unlock Card •••• {selectedCard?.last4}
            </DialogTitle>
            <DialogDescription className="text-center text-[#8892B0]">
              {user?.mfa_enabled 
                ? 'Enter your 2FA code to unlock this card'
                : 'Enter your panic PIN to unlock this card'
              }
            </DialogDescription>
          </DialogHeader>

          {user?.mfa_enabled && (
            <div className="py-4">
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={totpCode}
                  onChange={setTotpCode}
                  data-testid="unlock-card-otp-input"
                >
                  <InputOTPGroup className="gap-2">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <InputOTPSlot
                        key={index}
                        index={index}
                        className="w-12 h-14 text-xl font-bold bg-[#0B1C2D] border-[#2BBBAD]/30 text-[#E6F1FF] rounded-lg"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>
          )}

          {!user?.mfa_enabled && (
            <div className="py-4 flex justify-center">
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={12}
                value={unlockPin}
                onChange={(e) => setUnlockPin(e.target.value.replace(/\D/g, ''))}
                placeholder="Panic PIN"
                className="w-56 h-14 text-center text-xl tracking-widest bg-[#0B1C2D] border border-[#2BBBAD]/30 text-[#E6F1FF] rounded-lg"
                data-testid="unlock-card-pin-input"
              />
            </div>
          )}

          <DialogFooter className="flex gap-3 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setShowUnlockCardModal(false)}
              className="flex-1 border-[#64FFDA]/20 text-[#8892B0] hover:bg-[#64FFDA]/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUnlockCard}
              disabled={unlocking || (user?.mfa_enabled ? totpCode.length !== 6 : unlockPin.length < 6)}
              className="flex-1 bg-[#10B981] hover:bg-[#059669] text-white font-bold"
              data-testid="confirm-unlock-card-button"
            >
              {unlocking ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Unlock className="w-4 h-4 mr-2" />
                  Unlock Card
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
